import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/common/PageHeader';
import SEO from '../components/common/SEO';
import { getContentCreator, getAppUrls, getAppUrl } from '../utils/config';
import { useDirection } from '../contexts/DirectionContext';

const TermsOfService: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useDirection();
  const [creator, setCreator] = useState(getContentCreator());
  const [appUrls, setAppUrls] = useState(getAppUrls());
  const [appUrl, setAppUrl] = useState<string>('');

  useEffect(() => {
    // Update values if they change
    setCreator(getContentCreator());
    setAppUrls(getAppUrls());
    
    // Get the app URL based on platform
    const fetchAppUrl = async () => {
      try {
        const url = await getAppUrl();
        setAppUrl(url);
      } catch (error) {
        console.error('Error getting app URL:', error);
        setAppUrl(getAppUrls().website);
      }
    };
    
    fetchAppUrl();
  }, []);

  // Re-render when language changes
  useEffect(() => {
    // This effect ensures the component re-renders when language changes
  }, [i18n.language]);

  return (
    <div className="flex flex-col min-h-screen bg-background-primary">
      <SEO
        title={t('common.termsOfService')}
        description={t('common.termsOfServiceDescription')}
        keywords={[
          'Terms of Service',
          'Terms and Conditions',
          'User Agreement',
          'Legal Terms',
          'Service Terms'
        ]}
      />
      <PageHeader
        title={t('common.termsOfService')}
        description={t('common.termsOfServiceDescription')}
      />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mt-8 prose prose-lg max-w-none ltr text-left" dir="ltr">
          <div className="text-text-secondary mb-8">
            <p className="font-medium">Last Updated: June 12, 2024</p>
          </div>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">1. Introduction</h2>
            <p>
              Welcome to {creator.name}'s web app (the "Service"), accessible via our website ({appUrls.website}), Android app, and Apple app. These Terms of Service ("Terms") form a legally binding agreement between you ("User," "you," or "your") and {creator.organization} ("we," "us," or "our"), a company incorporated and operating under the laws of {creator.country}, which provides the technological infrastructure for the Service. {creator.name}, an Israeli citizen, is the primary content creator. By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, you must not use the Service.
            </p>
            <p className="mt-4">
              These Terms apply to all users worldwide. We are committed to compliance with applicable laws, including but not limited to:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li>{creator.country} law, such as the Personal Information Protection and Electronic Documents Act (PIPEDA).</li>
              <li>The European Union's General Data Protection Regulation (GDPR).</li>
              <li>Israel's Protection of Privacy Law, 5741-1981.</li>
              <li>U.S. federal and state laws, including the California Consumer Privacy Act (CCPA).</li>
            </ul>
            <p className="mt-4">
              Please read these Terms carefully, as they govern your use of the Service and define your rights and obligations.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">2. Acceptance of Terms</h2>
            <p>
              By accessing or using the Service, you confirm the following:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li>You are at least 16 years of age or have obtained parental consent if younger.</li>
              <li>You have the legal capacity to enter into this agreement.</li>
              <li>You agree to comply with all applicable local, national, and international laws and regulations.</li>
            </ul>
            <p className="mt-4">
              If you are using the Service on behalf of an organization or entity, you represent and warrant that you have the authority to bind that organization to these Terms, and "you" refers to both you and the organization.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">3. Description of the Service</h2>
            <p>
              The Service provides access to {creator.name}'s content, including videos, articles, and other educational or informational materials. It currently functions as a platform for viewing content, akin to a single-creator version of YouTube. We plan to expand the Service with the following features:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>User Accounts:</strong> Login functionality for personalized access.</li>
              <li><strong>Content Saving:</strong> Ability to save content for later viewing.</li>
              <li><strong>Offline Access:</strong> Downloading content for offline use.</li>
            </ul>
            <p className="mt-4">
              We reserve the right to modify, suspend, or discontinue any aspect of the Service—or introduce new features—at our sole discretion, with or without prior notice.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">4. User Accounts and Responsibilities</h2>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">4.1 Account Creation</h3>
            <p>
              When user login functionality is introduced, you will need to create an account to access enhanced features. You agree to:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Provide accurate, current, and complete information during registration.</li>
              <li>Update your information as needed to maintain its accuracy.</li>
            </ul>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">4.2 Account Security</h3>
            <ul className="list-disc pl-6">
              <li>You are solely responsible for safeguarding your login credentials (e.g., username and password).</li>
              <li>You must notify us immediately at {appUrls.privacyEmail} if you suspect unauthorized access to or use of your account.</li>
              <li>We are not liable for any loss or damage resulting from your failure to protect your credentials.</li>
            </ul>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">4.3 Prohibited Activities</h3>
            <p>
              You agree not to engage in any of the following:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Using the Service for illegal, fraudulent, or unauthorized purposes.</li>
              <li>Attempting to hack, reverse-engineer, or gain unauthorized access to the Service, its servers, or systems.</li>
              <li>Disrupting or interfering with the Service's operations, including through denial-of-service attacks.</li>
              <li>Uploading or transmitting viruses, malware, spyware, or any harmful code.</li>
              <li>Harassing, threatening, or harming other users, us, {creator.name}, or third parties.</li>
              <li>Engaging in any activity that could damage our reputation or that of {creator.name}.</li>
            </ul>
            <p className="mt-4">
              Violation of these prohibitions may result in immediate suspension or termination of your access.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">5. Content and Intellectual Property</h2>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">5.1 Ownership</h3>
            <p>
              <strong>Our Content:</strong> All content provided through the Service (e.g., videos, text, images, logos) is either owned by {creator.name} or licensed to {creator.organization}. This content is protected by {creator.country} and international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="mt-4">
              <strong>Your Content:</strong> If future features permit user-generated content (e.g., comments or reviews), you retain ownership of such content but grant us a worldwide, non-exclusive, royalty-free, sublicensable, and transferable license to use, reproduce, distribute, display, and modify it as necessary to operate and promote the Service.
            </p>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">5.2 License to Use the Service</h3>
            <p>
              We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service and its content for personal, non-commercial purposes, subject to compliance with these Terms.
            </p>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">5.3 Restrictions</h3>
            <p>
              You may not:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Copy, reproduce, distribute, publicly perform, or create derivative works of any content without our prior written consent.</li>
              <li>Use the Service or its content for commercial purposes unless explicitly authorized by us.</li>
              <li>Remove, obscure, or alter any copyright, trademark, or proprietary notices on the Service.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">6. Payments and Subscriptions</h2>
            <p>
              If we introduce paid features or subscription plans:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Fees:</strong> You agree to pay all applicable fees as displayed within the Service at the time of purchase.</li>
              <li><strong>Payment Processing:</strong> Payments will be handled through secure third-party payment processors. You authorize us to charge your chosen payment method.</li>
              <li><strong>Recurring Charges:</strong> Subscriptions may renew automatically, and you authorize recurring charges until you cancel your subscription through your account settings.</li>
              <li><strong>Price Changes:</strong> We may adjust pricing at any time, providing reasonable notice (e.g., via email or in-app notification).</li>
              <li><strong>Refunds:</strong> Unless required by applicable law, all payments are non-refundable.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">7. Privacy</h2>
            <p>
              Your privacy matters to us. Our collection, use, and protection of your personal information are governed by our <a href="/privacy-policy" className="text-primary-600 hover:text-primary-700">Privacy Policy</a>. Please review it for detailed information.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">8. Termination</h2>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">8.1 By You</h3>
            <p>
              You may discontinue using the Service at any time. Once accounts are implemented, you can delete your account via the account settings or by contacting us at {appUrls.privacyEmail}.
            </p>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">8.2 By Us</h3>
            <p>
              We may suspend or terminate your access to the Service, with or without notice, if:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>You breach these Terms.</li>
              <li>Your actions pose a risk to us, {creator.name}, other users, or the Service's integrity.</li>
              <li>We are compelled to do so by law or a court order.</li>
            </ul>
            <p className="mt-4">
              Upon termination, your license to use the Service ends immediately. Provisions intended to survive termination (e.g., intellectual property, limitation of liability) will remain in effect.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">9. Disclaimers and Limitation of Liability</h2>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">9.1 Disclaimers</h3>
            <p>
              The Service is provided "as is" and "as available," without warranties of any kind, whether express, implied, or statutory, including:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Merchantability, fitness for a particular purpose, or non-infringement.</li>
              <li>Availability, accuracy, reliability, or completeness of content.</li>
              <li>Absence of viruses, errors, or other harmful components.</li>
            </ul>
            <p className="mt-4">
              We do not guarantee uninterrupted, secure, or error-free operation of the Service.
            </p>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">9.2 Limitation of Liability</h3>
            <p>
              To the fullest extent permitted by law, {creator.organization}, {creator.name}, and their affiliates, officers, directors, employees, and agents shall not be liable for:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Indirect, incidental, special, consequential, or punitive damages (e.g., lost profits, data, or goodwill).</li>
              <li>Any damages arising from your use of or inability to use the Service.</li>
              <li>Third-party content, conduct, or unauthorized access to your data.</li>
            </ul>
            <p className="mt-4">
              Our aggregate liability for any claim related to the Service shall not exceed the greater of:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>The amount you paid us in the 12 months prior to the claim.</li>
              <li>CAD $100.</li>
            </ul>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">9.3 Jurisdictional Exceptions</h3>
            <p>
              If your jurisdiction does not allow certain disclaimers or limitations, our liability will be limited to the maximum extent permitted by applicable law.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless {creator.organization}, {creator.name}, and their affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses (including reasonable legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li>Your use of the Service.</li>
              <li>Your violation of these Terms or applicable laws.</li>
              <li>Your infringement of any third-party rights (e.g., intellectual property or privacy).</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">11. Governing Law and Dispute Resolution</h2>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">11.1 Governing Law</h3>
            <p>
              These Terms are governed by and construed in accordance with the laws of the Province of Ontario, {creator.country}, without regard to its conflict of law principles.
            </p>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">11.2 Dispute Resolution</h3>
            <ul className="list-disc pl-6">
              <li><strong>Informal Resolution:</strong> You agree to first attempt to resolve any dispute informally by contacting us at {appUrls.privacyEmail}. We will respond within 30 days.</li>
              <li><strong>Arbitration:</strong> If unresolved, disputes shall be settled by binding arbitration in Toronto, {creator.country}, under the rules of the Canadian Arbitration Association. The arbitrator's decision is final and enforceable in court.</li>
              <li><strong>Exceptions:</strong> Either party may seek injunctive or equitable relief in a court of competent jurisdiction for intellectual property violations or urgent legal matters.</li>
            </ul>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">11.3 Class Action Waiver</h3>
            <p>
              You agree to resolve disputes on an individual basis and waive any right to participate in class actions, class arbitrations, or representative actions.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">12. International Users</h2>
            <p>
              The Service is hosted and operated from {creator.country} but designed for global use. If you access it from outside {creator.country}:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li>You are responsible for compliance with your local laws.</li>
              <li>EU users are entitled to GDPR protections as detailed in our Privacy Policy.</li>
              <li>We make no representation that the Service is appropriate or available in all locations.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">13. Changes to These Terms</h2>
            <p>
              We may update these Terms to reflect changes in the Service, legal requirements, or business practices. We will notify you of material changes via email, in-app notifications, or on our website at least 30 days before they take effect (unless immediate changes are legally required). Your continued use of the Service after updates signifies your acceptance. The "Last Updated" date at the top will indicate the latest revision.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">14. Miscellaneous</h2>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy, constitute the full agreement between you and us regarding the Service, superseding any prior agreements.</li>
              <li><strong>Severability:</strong> If any provision is deemed invalid or unenforceable, the remaining provisions will remain in full force.</li>
              <li><strong>Waiver:</strong> Our failure to enforce any right or provision does not constitute a waiver of that right or provision.</li>
              <li><strong>Assignment:</strong> You may not assign these Terms without our prior written consent. We may assign them freely, such as in the case of a merger or acquisition.</li>
              <li><strong>Force Majeure:</strong> We are not liable for delays or failures in performance due to events beyond our reasonable control (e.g., natural disasters, war, or internet outages).</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">15. Contact Us</h2>
            <p>
              For questions, support, or feedback regarding these Terms or the Service, please contact:
            </p>
            <div className="mt-4 p-6 bg-surface-secondary rounded-lg border border-border-primary">
              <p className="font-semibold mb-2">{creator.organization}</p>
              <p>#203-1929 West Broadway</p>
              <p>Vancouver, British Columbia, {creator.country}</p>
              <p>V6J 1Z3</p>
              <p className="mt-4"><strong>Email:</strong> {appUrls.privacyEmail}</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService; 